import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProviderDevices } from '../services/providerDeviceService';
import { logger } from '../logger';
import providerService from '../services/providerService';

const INITIAL_LOAD_LIMIT = 50;
const LOAD_MORE_LIMIT = 50;
const PROVIDER_DEVICES_QUERY_KEY = 'providerDevices';
const backgroundFetchTasks = new Map();

const getQueryKey = (providerId) => [
  PROVIDER_DEVICES_QUERY_KEY,
  providerId,
];

const getHasMoreFromResponse = (response) =>
  response?.hasMorePages ??
  (response?.devices?.length ?? 0) === LOAD_MORE_LIMIT;

const getNextPageFromData = (data) => {
  if (typeof data?.nextPage === 'number') return data.nextPage;

  const loadedCount = data?.devices?.length ?? 0;
  return Math.max(1, Math.ceil(loadedCount / LOAD_MORE_LIMIT));
};

const mergeDevices = (existingDevices = [], incomingDevices = []) => {
  const devicesById = new Map(
    existingDevices.map((device) => [device.id, device]),
  );

  incomingDevices.forEach((device) => {
    const existingDevice = devicesById.get(device.id);
    devicesById.set(
      device.id,
      existingDevice ? { ...existingDevice, ...device } : device,
    );
  });

  return Array.from(devicesById.values());
};

const mergeQueryData = (
  existingData,
  incomingDevices,
  hasMorePages,
  nextPage,
) => ({
  ...existingData,
  devices: mergeDevices(existingData?.devices, incomingDevices),
  hasMorePages,
  nextPage,
});

const cancelBackgroundFetch = (providerId) => {
  const task = backgroundFetchTasks.get(providerId);

  if (!task) return;

  task.cancelled = true;
  backgroundFetchTasks.delete(providerId);
};

const ensureBackgroundFetch = ({ providerId, queryClient, log }) => {
  if (!providerId) return null;

  const existingTask = backgroundFetchTasks.get(providerId);
  if (existingTask) return existingTask.promise;

  const task = {
    cancelled: false,
    promise: null,
  };

  task.promise = (async () => {
    const queryKey = getQueryKey(providerId);

    try {
      let hasMore = true;

      while (hasMore && !task.cancelled) {
        const cachedData = queryClient.getQueryData(queryKey);
        const pageToFetch = getNextPageFromData(cachedData);
        // eslint-disable-next-line no-await-in-loop
        const response = await getProviderDevices(
          providerId,
          pageToFetch,
          LOAD_MORE_LIMIT,
        );

        if (task.cancelled) break;

        hasMore = getHasMoreFromResponse(response);

        if (!response?.devices?.length) {
          const existingData = queryClient.getQueryData(queryKey);
          queryClient.setQueryData(queryKey, {
            ...existingData,
            hasMorePages: false,
            nextPage: pageToFetch,
          });
          break;
        }

        const existingData = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(
          queryKey,
          mergeQueryData(
            existingData,
            response.devices,
            hasMore,
            pageToFetch + 1,
          ),
        );
      }
    } catch (error) {
      log.error('Failed to background fetch provider devices', error);
    } finally {
      backgroundFetchTasks.delete(providerId);
    }
  })();

  backgroundFetchTasks.set(providerId, task);
  return task.promise;
};

/**
 * Custom hook for fetching provider devices using React Query.
 *
 * - Uses `useQuery` for the initial page (page 0) with caching and stale-while-revalidate.
 * - Silently fetches remaining pages in the background after initial load.
 * - Tracks accumulated devices in a ref for client-side filtering.
 *
 * @param {Object} [options] - Additional React Query options
 * @returns {Object} Query state, pagination controls, and data management utilities
 */
export const useProviderDevices = (options = {}) => {
  const { providerId } = providerService.getProviderDetails() ?? {};
  const queryClient = useQueryClient();
  const loanerDataRef = useRef([]);
  const filteredLoanerDataRef = useRef([]);
  const hasMoreRef = useRef(true);
  const previousProviderIdRef = useRef(providerId);
  const hasSyncedRef = useRef(false);
  const [hasSynced, setHasSynced] = useState(false);
  const [syncVersion, setSyncVersion] = useState(0);
  const [isBackgroundFetching, setIsBackgroundFetching] =
    useState(false);

  const log = logger();

  const query = useQuery({
    queryKey: getQueryKey(providerId),
    queryFn: () =>
      getProviderDevices(providerId, 0, INITIAL_LOAD_LIMIT),
    enabled: Boolean(providerId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });

  // Filter out devices that should not be visible to the current clinic:
  // 1. Devices owned by another clinic (owner exists with a different cochlearId).
  // 2. "Exchange Stock" devices owned by a Patient.
  const filterVisibleDevices = useCallback(
    (list) =>
      list.filter((d) => {
        if (
          d.status === 'Exchange Stock' &&
          d.owner &&
          d.owner.type === 'Patient'
        ) {
          return false;
        }
        return (
          !d.owner ||
          !d.owner.cochlearId ||
          d.owner.type === 'Patient' ||
          d.owner.cochlearId === providerId
        );
      }),
    [providerId],
  );

  const updateDeviceRefs = useCallback(
    (devices) => {
      loanerDataRef.current = devices;
      filteredLoanerDataRef.current = filterVisibleDevices(devices);
    },
    [filterVisibleDevices],
  );

  const resetState = useCallback(() => {
    hasSyncedRef.current = false;
    loanerDataRef.current = [];
    filteredLoanerDataRef.current = [];
    hasMoreRef.current = true;
    setHasSynced(false);
    setSyncVersion(0);
    setIsBackgroundFetching(false);
  }, []);

  // Reset when provider changes.
  useEffect(() => {
    if (
      previousProviderIdRef.current &&
      previousProviderIdRef.current !== providerId
    ) {
      cancelBackgroundFetch(previousProviderIdRef.current);
    }
    previousProviderIdRef.current = providerId;
    resetState();
  }, [providerId, resetState]);

  // Sync query data to accumulated device refs.
  // Initial sync seeds the refs; subsequent syncs (e.g. after mutation
  // invalidation) merge fresh data so statuses stay current.
  useEffect(() => {
    if (!query.data) return;

    const devices = query.data.devices ?? [];
    const hasMore =
      query.data.hasMorePages ??
      devices.length === INITIAL_LOAD_LIMIT;
    const isInitialSync = !hasSyncedRef.current;

    if (isInitialSync && !devices.length) {
      updateDeviceRefs([]);
      hasMoreRef.current = false;
      hasSyncedRef.current = true;
      setHasSynced(true);
      return;
    }

    const merged = mergeDevices(loanerDataRef.current, devices);

    if (
      merged.length !== loanerDataRef.current.length ||
      devices.length !== 0
    ) {
      updateDeviceRefs(merged);
      if (!isInitialSync) setSyncVersion((v) => v + 1);
    }

    hasMoreRef.current = hasMore;

    // When background fetch writes hasMorePages:false to the cache,
    // this effect fires and turns off the spinner for every hook instance.
    if (!isInitialSync && !hasMore) {
      setIsBackgroundFetching(false);
    }

    if (isInitialSync) {
      hasSyncedRef.current = true;
      setHasSynced(true);
    }
  }, [query.data, updateDeviceRefs]);

  // Start background fetch after initial sync if more pages exist.
  useEffect(() => {
    if (!providerId || !hasSynced || !hasMoreRef.current) return;
    setIsBackgroundFetching(true);
    ensureBackgroundFetch({
      providerId,
      queryClient,
      log,
    });
  }, [providerId, hasSynced, queryClient, log]);

  // hasSynced gates 'loaded' so DataTable never initialises with
  // an empty data set.
  const renderState = useMemo(() => {
    if (query.isLoading || !hasSynced) return 'loading';
    if (query.isSuccess && (query.data?.devices?.length ?? 0) > 0)
      return 'loaded';
    if (query.isSuccess || query.isError) return 'noresults';
    return undefined;
  }, [
    query.isLoading,
    query.isSuccess,
    query.isError,
    query.data,
    hasSynced,
  ]);

  const updateDeviceInRef = useCallback((deviceId, updatedData) => {
    loanerDataRef.current = loanerDataRef.current.map((device) =>
      device.id === deviceId ? { ...device, ...updatedData } : device,
    );
    filteredLoanerDataRef.current = filteredLoanerDataRef.current.map(
      (device) =>
        device.id === deviceId
          ? { ...device, ...updatedData }
          : device,
    );
  }, []);

  /**
   * Resets all sync state back to 'loading', clears cached expanded-device
   * data, and refetches the initial page.
   */
  const resetAndRefetch = useCallback(
    (assetId) => {
      cancelBackgroundFetch(providerId);
      resetState();
      const expandedKey = assetId
        ? ['providerDeviceExpanded', providerId, assetId]
        : ['providerDeviceExpanded', providerId];
      queryClient.removeQueries({ queryKey: expandedKey });
      query.refetch();
    },
    [query, queryClient, providerId, resetState],
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    resetAndRefetch,
    renderState,
    isBackgroundFetching,
    loanerDataRef: filteredLoanerDataRef,
    updateDeviceInRef,
    syncVersion,
  };
};
